import { CAPABILITY } from "../../../helpers/permissions";
import { VOICE_INTENT_CATEGORY, VOICE_WORKFLOW } from "../../ashleyInterfaceTypes";
import { ASHLEY_PROMPTS } from "../../speech/ashleyPrompts";

export const SCREEN_TARGET = Object.freeze({
  KPI_DASHBOARD: "kpi_dashboard",
  CREATE_PROJECT: "create_project",
  CREATE_CLIENT: "create_client",
  ESTIMATE_HUB: "estimate_hub",
  ESTIMATE_PIPELINE: "estimate_pipeline",
  INVOICE_HUB: "invoice_hub",
  PAYROLL_QUEUE: "payroll_queue",
  PAYROLL_HOME: "payroll_home",
  INTELLIGENCE: "intelligence",
  MISSION_CONTROL: "mission_control",
  ADMINISTRATION: "administration",
  SETTINGS: "settings",
  TIME_TRACKING_V2: "time_tracking_v2",
  MANUAL_TIME_ENTRY: "manual_time_entry",
  CREWPILOT: "crewpilot",
  MATERIALS: "materials",
  ACCOUNTS_RECEIVABLE: "accounts_receivable",
  ACCOUNTS_PAYABLE: "accounts_payable",
  MODE_SELECT: "mode_select",
});

export const HANDLER_TARGET = Object.freeze({
  OPEN_PROJECTS_HUB: "openProjectsHub",
  OPEN_NEW_PROJECT: "openNewProject",
  OPEN_CLIENTS_HUB: "openClientsHub",
  OPEN_NEW_CLIENT: "openNewClient",
  OPEN_ESTIMATE_HUB: "openEstimateHub",
  OPEN_NEW_ESTIMATE: "openNewEstimate",
  START_BREAK: "startBreak",
  STOP_BREAK: "stopBreak",
  SIGN_OUT: "signOut",
  CHECK_RECENT_EMAILS: "checkRecentEmails",
  OPEN_EMAIL_INBOX: "openEmailInbox",
  CHECK_SPAM_ATTENTION: "checkSpamAttention",
  REVIEW_ALL_UNREAD_EMAILS: "reviewAllUnreadEmails",
  LAUNCH_EXECUTIVE_BRIEFING: "launchExecutiveBriefing",
});

/**
 * @typedef {object} VoiceIntentDefinition
 * @property {string} id
 * @property {string} category
 * @property {string} label
 * @property {string[]} phrases
 * @property {string} [capability]
 * @property {string[]} [preconditions]
 * @property {{ type: "screen", screen: string, section?: string } | { type: "handler", handler: string } | { type: "workflow", screen: string, workflow: string }} target
 * @property {{ success?: string, denied?: string, failed?: string }} [feedback]
 */

/** @type {VoiceIntentDefinition[]} */
export const VOICE_INTENT_REGISTRY = [
  {
    id: "nav.home",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Home",
    phrases: ["home", "go home", "take me home"],
    capability: CAPABILITY.SCREEN_KPI_DASHBOARD,
    target: { type: "screen", screen: SCREEN_TARGET.KPI_DASHBOARD },
    feedback: { success: "Opening dashboard." },
  },
  {
    id: "nav.dashboard",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Dashboard",
    phrases: [
      "dashboard",
      "dashboards",
      "open dashboard",
      "open dashboards",
      "go to dashboard",
      "go to dashboards",
      "kpi dashboard",
      "show dashboard",
      "show dashboards",
    ],
    capability: CAPABILITY.SCREEN_KPI_DASHBOARD,
    target: { type: "screen", screen: SCREEN_TARGET.KPI_DASHBOARD },
    feedback: { success: "Opening dashboard." },
  },
  {
    id: "nav.projects",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Projects",
    phrases: [
      "projects",
      "open projects",
      "show projects",
      "go to projects",
      "project list",
    ],
    capability: CAPABILITY.SCREEN_CREATE_PROJECT,
    target: { type: "handler", handler: HANDLER_TARGET.OPEN_PROJECTS_HUB },
    feedback: { success: "Opening projects." },
  },
  {
    id: "nav.clients",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Clients",
    phrases: [
      "clients",
      "open clients",
      "show clients",
      "go to clients",
      "client list",
    ],
    capability: CAPABILITY.SCREEN_CREATE_CLIENT,
    target: { type: "handler", handler: HANDLER_TARGET.OPEN_CLIENTS_HUB },
    feedback: { success: "Opening clients." },
  },
  {
    id: "nav.create_client",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Create Client",
    phrases: [
      "create client",
      "create a client",
      "create new client",
      "create a new client",
      "new client",
      "add client",
    ],
    capability: CAPABILITY.SCREEN_CREATE_CLIENT,
    target: { type: "handler", handler: HANDLER_TARGET.OPEN_NEW_CLIENT },
    feedback: { success: "Opening new client." },
  },
  {
    id: "nav.create_project",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Create Project",
    phrases: [
      "create job",
      "create a job",
      "create new job",
      "create a new job",
      "new job",
      "add job",
      "add a job",
      "start a new job",
      "start new job",
      "open new job",
      "job builder",
      "new project",
      "create project",
      "create a project",
      "create new project",
      "create a new project",
      "add project",
      "add a project",
    ],
    capability: CAPABILITY.SCREEN_CREATE_PROJECT,
    target: { type: "handler", handler: HANDLER_TARGET.OPEN_NEW_PROJECT },
    feedback: { success: "Opening new project." },
  },
  {
    id: "nav.estimates",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Estimates",
    phrases: [
      "estimates",
      "open estimates",
      "show estimates",
      "go to estimates",
      "estimate hub",
    ],
    capability: CAPABILITY.SCREEN_ESTIMATE_HUB,
    target: { type: "handler", handler: HANDLER_TARGET.OPEN_ESTIMATE_HUB },
    feedback: { success: "Opening estimates." },
  },
  {
    id: "nav.create_estimate",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Create Estimate",
    phrases: [
      "create estimate",
      "create an estimate",
      "create new estimate",
      "create a new estimate",
      "new estimate",
      "add estimate",
    ],
    capability: CAPABILITY.SCREEN_ESTIMATE_HUB,
    target: { type: "handler", handler: HANDLER_TARGET.OPEN_NEW_ESTIMATE },
    feedback: { success: "Opening new estimate." },
  },
  {
    id: "nav.payroll",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Payroll",
    // Generic "payroll" now lands on Payroll Home (the canonical landing
    // destination, matching More → Payroll). Use "payroll queue" for the
    // queue directly — see nav.payroll_queue below.
    phrases: ["payroll", "open payroll", "go to payroll", "show payroll"],
    capability: CAPABILITY.SCREEN_PAYROLL_HOME,
    target: { type: "screen", screen: SCREEN_TARGET.PAYROLL_HOME },
    feedback: { success: "Opening payroll." },
  },
  {
    id: "nav.payroll_queue",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Payroll Queue",
    phrases: ["payroll queue", "open payroll queue", "go to payroll queue", "show payroll queue"],
    capability: CAPABILITY.PAYROLL_QUEUE,
    target: { type: "screen", screen: SCREEN_TARGET.PAYROLL_QUEUE },
    feedback: { success: "Opening payroll queue." },
  },
  {
    id: "nav.reports",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Reports",
    phrases: [
      "reports",
      "open reports",
      "go to reports",
      "show reports",
      "intelligence",
      "report center",
    ],
    capability: CAPABILITY.SCREEN_INTELLIGENCE,
    target: { type: "screen", screen: SCREEN_TARGET.INTELLIGENCE },
    feedback: { success: "Opening reports." },
  },
  {
    id: "nav.mission_control",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Mission Control",
    phrases: [
      "mission control",
      "open mission control",
      "go to mission control",
      "show mission control",
    ],
    capability: CAPABILITY.SCREEN_MISSION_CONTROL,
    target: { type: "screen", screen: SCREEN_TARGET.MISSION_CONTROL },
    feedback: { success: "Opening mission control." },
  },
  {
    id: "nav.settings",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Settings",
    // "Settings" is the unified, role-filtered hub (personal preferences +
    // whatever organization configuration the current user has access to).
    // "Administration" is preserved as a conversational/backward-compatible
    // synonym for the same destination — it no longer opens a separate,
    // manager/admin-only screen. Visibility inside Settings stays
    // capability-filtered; this alias does not bypass permissions.
    phrases: [
      "settings",
      "open settings",
      "go to settings",
      "preferences",
      "open preferences",
      "my settings",
      "administration",
      "open administration",
      "go to administration",
    ],
    capability: CAPABILITY.SCREEN_SETTINGS,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS },
    feedback: { success: "Opening settings." },
  },
  {
    id: "nav.invoices",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Invoices",
    phrases: [
      "invoices",
      "open invoices",
      "show invoices",
      "go to invoices",
      "invoice hub",
    ],
    capability: CAPABILITY.SCREEN_INVOICE_HUB,
    target: { type: "screen", screen: SCREEN_TARGET.INVOICE_HUB },
    feedback: { success: "Opening invoices." },
  },
  {
    id: "nav.accounts_receivable",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Accounts Receivable",
    phrases: [
      "accounts receivable",
      "open accounts receivable",
      "show accounts receivable",
      "go to accounts receivable",
      "receivables",
    ],
    capability: CAPABILITY.SCREEN_ACCOUNTS_RECEIVABLE,
    target: { type: "screen", screen: SCREEN_TARGET.ACCOUNTS_RECEIVABLE },
    feedback: { success: "Opening accounts receivable." },
  },
  {
    id: "nav.accounts_payable",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Accounts Payable",
    phrases: [
      "accounts payable",
      "open accounts payable",
      "show accounts payable",
      "go to accounts payable",
      "payables",
    ],
    capability: CAPABILITY.SCREEN_ACCOUNTS_PAYABLE,
    target: { type: "screen", screen: SCREEN_TARGET.ACCOUNTS_PAYABLE },
    feedback: { success: "Opening accounts payable." },
  },
  {
    id: "nav.materials",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Materials",
    phrases: [
      "materials",
      "open materials",
      "show materials",
      "go to materials",
      "materials hub",
    ],
    capability: CAPABILITY.SCREEN_MATERIALS,
    target: { type: "screen", screen: SCREEN_TARGET.MATERIALS },
    feedback: { success: "Opening materials." },
  },
  {
    id: "nav.time_tracking",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Time Tracking",
    phrases: [
      "time tracking",
      "open time tracking",
      "show time tracking",
      "go to time tracking",
    ],
    capability: CAPABILITY.SCREEN_TIME_TRACKING,
    target: { type: "screen", screen: SCREEN_TARGET.TIME_TRACKING_V2 },
    feedback: { success: "Opening time tracking." },
  },
  {
    id: "nav.manual_time_entry",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Time Logs",
    phrases: [
      "time logs",
      "open time logs",
      "show time logs",
      "manual time entry",
      "open manual time entry",
      "log time manually",
    ],
    capability: CAPABILITY.SCREEN_MANUAL_TIME_ENTRY,
    target: { type: "screen", screen: SCREEN_TARGET.MANUAL_TIME_ENTRY },
    feedback: { success: "Opening time logs." },
  },
  {
    id: "nav.command_center",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Command Center",
    phrases: [
      "command center",
      "open command center",
      "go to command center",
      "cockpit",
      "open cockpit",
      "go to cockpit",
    ],
    capability: CAPABILITY.SCREEN_COCKPIT,
    target: { type: "screen", screen: SCREEN_TARGET.CREWPILOT },
    feedback: { success: "Opening command center." },
  },
  {
    id: "nav.estimate_pipeline",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Estimate Pipeline",
    phrases: [
      "estimate pipeline",
      "open estimate pipeline",
      "show estimate pipeline",
      "go to estimate pipeline",
    ],
    capability: CAPABILITY.ESTIMATE_VIEW,
    target: { type: "screen", screen: SCREEN_TARGET.ESTIMATE_PIPELINE },
    feedback: { success: "Opening estimate pipeline." },
  },
  {
    id: "nav.payroll_home",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Payroll Home",
    phrases: ["payroll home", "open payroll home", "go to payroll home"],
    capability: CAPABILITY.SCREEN_PAYROLL_HOME,
    target: { type: "screen", screen: SCREEN_TARGET.PAYROLL_HOME },
    feedback: { success: "Opening payroll." },
  },
  {
    id: "nav.mode_select",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "All Modules",
    phrases: [
      "all modules",
      "open all modules",
      "go to all modules",
      "modules",
      "module select",
      "select module",
    ],
    capability: CAPABILITY.SCREEN_MODE_SELECT,
    target: { type: "screen", screen: SCREEN_TARGET.MODE_SELECT },
    feedback: { success: "Opening modules." },
  },
  {
    id: "nav.admin.organization",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Organization",
    phrases: ["organization", "open organization", "go to organization", "org settings"],
    capability: CAPABILITY.ADMIN_ORGANIZATION_MANAGE,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "organization" },
    feedback: { success: "Opening organization." },
  },
  {
    id: "nav.admin.branding",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Branding",
    phrases: ["branding", "open branding", "go to branding", "brand settings"],
    capability: CAPABILITY.ADMIN_BRANDING_MANAGE,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "branding" },
    feedback: { success: "Opening branding." },
  },
  {
    id: "nav.admin.users",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Users",
    phrases: ["users", "open users", "show users", "go to users", "user management"],
    capability: CAPABILITY.SCREEN_USER_MANAGEMENT,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "users" },
    feedback: { success: "Opening users." },
  },
  {
    id: "nav.admin.invitations",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Invitations",
    phrases: [
      "invitations",
      "open invitations",
      "go to invitations",
      "organization invitations",
    ],
    capability: CAPABILITY.ADMIN_ORGANIZATION_MANAGE,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "invitations" },
    feedback: { success: "Opening invitations." },
  },
  {
    id: "nav.admin.trades",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Trades",
    phrases: ["trades", "open trades", "go to trades"],
    capability: CAPABILITY.ADMIN_TRADES_MANAGE,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "trades" },
    feedback: { success: "Opening trades." },
  },
  {
    id: "nav.admin.classifications",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Classifications",
    phrases: ["classifications", "open classifications", "go to classifications"],
    capability: CAPABILITY.ADMIN_CLASSIFICATIONS_MANAGE,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "classifications" },
    feedback: { success: "Opening classifications." },
  },
  {
    id: "nav.admin.payroll_types",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Payroll Types",
    phrases: ["payroll types", "open payroll types", "go to payroll types"],
    capability: CAPABILITY.ADMIN_PAYROLL_TYPES_MANAGE,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "payroll_types" },
    feedback: { success: "Opening payroll types." },
  },
  {
    id: "nav.admin.payroll_rates",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Payroll Rates",
    phrases: ["payroll rates", "open payroll rates", "go to payroll rates"],
    capability: CAPABILITY.ADMIN_PAYROLL_SETTINGS,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "payroll" },
    feedback: { success: "Opening payroll rates." },
  },
  {
    id: "nav.admin.work_types",
    category: VOICE_INTENT_CATEGORY.NAVIGATION,
    label: "Work Types",
    phrases: ["work types", "open work types", "go to work types"],
    capability: CAPABILITY.ADMIN_WORK_TYPES_MANAGE,
    target: { type: "screen", screen: SCREEN_TARGET.SETTINGS, section: "work_types" },
    feedback: { success: "Opening work types." },
  },
  {
    id: "workflow.start_time",
    category: VOICE_INTENT_CATEGORY.WORKFLOW,
    label: "Start Time",
    phrases: ["start time", "start session", "clock in", "begin session"],
    capability: CAPABILITY.SCREEN_TIME_TRACKING,
    target: {
      type: "workflow",
      screen: SCREEN_TARGET.TIME_TRACKING_V2,
      workflow: VOICE_WORKFLOW.OPEN_START_SESSION,
    },
    feedback: { success: "Perfect. Let's get you started with a new session." },
  },
  {
    id: "workflow.stop_time",
    category: VOICE_INTENT_CATEGORY.WORKFLOW,
    label: "Stop Time",
    phrases: ["stop time", "stop session", "clock out", "end session"],
    capability: CAPABILITY.SCREEN_TIME_TRACKING,
    target: {
      type: "workflow",
      screen: SCREEN_TARGET.TIME_TRACKING_V2,
      workflow: VOICE_WORKFLOW.OPEN_STOP_SESSION,
    },
    feedback: {
      success: "Opening stop session.",
      failed: ASHLEY_PROMPTS.NO_ACTIVE_SESSION,
    },
  },
  {
    id: "workflow.add_materials",
    category: VOICE_INTENT_CATEGORY.WORKFLOW,
    label: "Add Materials",
    phrases: ["add materials", "add material", "log materials", "log material"],
    capability: CAPABILITY.SCREEN_TIME_TRACKING,
    preconditions: [],
    target: {
      type: "workflow",
      screen: SCREEN_TARGET.TIME_TRACKING_V2,
      workflow: VOICE_WORKFLOW.OPEN_MATERIALS_PICKER,
    },
    feedback: {
      success: "Opening materials.",
      failed: ASHLEY_PROMPTS.NO_ACTIVE_SESSION_MATERIALS,
    },
  },
  {
    id: "action.start_break",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Start Break",
    phrases: ["start break", "take a break", "begin break"],
    capability: CAPABILITY.SCREEN_TIME_TRACKING,
    preconditions: ["active_session", "break_inactive"],
    target: { type: "handler", handler: HANDLER_TARGET.START_BREAK },
    feedback: {
      success: ASHLEY_PROMPTS.START_BREAK_SUCCESS,
      failed: ASHLEY_PROMPTS.NO_ACTIVE_SESSION,
    },
  },
  {
    id: "action.stop_break",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Stop Break",
    phrases: ["stop break", "end break", "resume work", "back to work"],
    capability: CAPABILITY.SCREEN_TIME_TRACKING,
    preconditions: ["active_session", "break_active"],
    target: { type: "handler", handler: HANDLER_TARGET.STOP_BREAK },
    feedback: {
      success: ASHLEY_PROMPTS.STOP_BREAK_SUCCESS,
      failed: ASHLEY_PROMPTS.NOT_ON_BREAK,
    },
  },
  {
    id: "nav.sign_out",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Sign Out",
    phrases: ["sign out", "sign me out"],
    target: { type: "handler", handler: HANDLER_TARGET.SIGN_OUT },
    feedback: { success: "Signing out. Have a wonderful day." },
  },
  {
    id: "action.email.check",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Check Emails",
    phrases: [
      "check my emails",
      "check my email",
      "check my recent emails",
      "check recent emails",
      "what emails do i have",
      "what email do i have",
      "what new emails do i have",
      "what new email do i have",
      "do i have any new emails",
      "do i have any new email",
      "do i have new emails",
      "any new emails",
      "any new email",
      "any emails",
      "any email",
      "whats in my inbox",
      "what's in my inbox",
      "what is in my inbox",
      "whats in my email",
      "what's in my email",
      "tell me about my emails",
      "tell me about my email",
      "tell me about my inbox",
      "catch me up on my email",
      "catch me up on my emails",
      "summarize my inbox",
      "summarize my emails",
      "summarize my recent emails",
      "read my recent emails",
      "read my emails",
    ],
    target: { type: "handler", handler: HANDLER_TARGET.CHECK_RECENT_EMAILS },
    feedback: {
      failed: ASHLEY_PROMPTS.EMAIL_NOT_CONNECTED,
    },
  },
  {
    id: "action.email.open",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Open Emails",
    phrases: [
      "open my emails",
      "open my email",
      "open my inbox",
      "open inbox",
      "open gmail",
      "open my gmail",
      "launch gmail",
      "go to my inbox",
      "go to my emails",
    ],
    target: { type: "handler", handler: HANDLER_TARGET.OPEN_EMAIL_INBOX },
    feedback: {
      success: ASHLEY_PROMPTS.EMAIL_OPENING,
      failed: ASHLEY_PROMPTS.EMAIL_NOT_CONNECTED,
    },
  },
  {
    id: "action.email.spam_check",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Check Spam Attention",
    phrases: [
      "check my spam",
      "check spam",
      "check the spam folder",
      "review my spam",
      "review spam",
      "anything important in spam",
      "anything in spam",
      "did anything important go to spam",
      "did anything go to spam",
      "is there anything important in spam",
      "look at my spam",
      "look in spam",
      "check for spam mistakes",
      "did spam catch anything important",
    ],
    target: { type: "handler", handler: HANDLER_TARGET.CHECK_SPAM_ATTENTION },
    feedback: {
      failed: ASHLEY_PROMPTS.EMAIL_NOT_CONNECTED,
    },
  },
  {
    id: "action.email.review_all_unread",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Review All Unread Emails",
    phrases: [
      "review all unread",
      "review all unread emails",
      "list all unread emails",
      "list all unread",
      "show all unread",
      "show all unread emails",
      "who else emailed me",
      "who emailed me",
      "show me everyone",
      "read all senders",
      "list all senders",
      "show me all unread senders",
      "list every unread sender",
    ],
    target: { type: "handler", handler: HANDLER_TARGET.REVIEW_ALL_UNREAD_EMAILS },
    feedback: {
      failed: ASHLEY_PROMPTS.EMAIL_NOT_CONNECTED,
    },
  },
  {
    id: "action.executive_briefing",
    category: VOICE_INTENT_CATEGORY.ACTION,
    label: "Executive Briefing",
    phrases: [
      "executive briefing",
      "give me my executive briefing",
      "give me the executive briefing",
      "give me an executive briefing",
      "start my executive briefing",
      "start the executive briefing",
      "start executive briefing",
      "open my executive briefing",
      "open the executive briefing",
      "open executive briefing",
      "play my executive briefing",
      "play the executive briefing",
      "morning briefing",
      "give me my morning briefing",
      "give me the morning briefing",
      "start my morning briefing",
      "daily briefing",
      "give me my daily briefing",
      "give me the daily briefing",
      "start my daily briefing",
      "start my briefing",
      "start the briefing",
      "start briefing",
      "give me my briefing",
      "give me the briefing",
      "give me a briefing",
      "brief me",
      "brief me today",
      "brief me on today",
      "catch me up",
      "catch me up today",
      "catch me up on today",
      "good morning",
      "what's happening today",
      "whats happening today",
      "what is happening today",
      "what's going on today",
      "whats going on today",
      "what is going on today",
      "what's new today",
      "whats new today",
      "what is new today",
      "give me today's overview",
      "give me todays overview",
      "today's overview",
      "todays overview",
      "today's briefing",
      "todays briefing",
      "give me today's briefing",
      "give me todays briefing",
    ],
    capability: CAPABILITY.SCREEN_KPI_DASHBOARD,
    target: { type: "handler", handler: HANDLER_TARGET.LAUNCH_EXECUTIVE_BRIEFING },
    feedback: {
      success: ASHLEY_PROMPTS.EXECUTIVE_BRIEFING_START,
    },
  },
];

export function getVoiceIntentById(intentId) {
  return VOICE_INTENT_REGISTRY.find((intent) => intent.id === intentId) || null;
}
